import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  BarChart3,
  Barcode,
  Boxes,
  Building2,
  Check,
  GitCompare,
  History,
  Mail,
  MessageSquare,
  Package,
  Percent,
  Phone,
  QrCode,
  Receipt,
  RotateCcw,
  Send,
  ShieldCheck,
  ShoppingCart,
  Sparkles,
  Store,
  TrendingUp,
  Truck,
  Users,
  Zap,
} from "lucide-react";
import BrandMark from "@/components/BrandMark";
import PlanFeatureMatrix from "@/components/PlanFeatureMatrix";
import { api, formatIDR } from "@/lib/api";

const featureIcons = {
  pos: ShoppingCart,
  sales_history: Receipt,
  refunds: RotateCcw,
  tax_ppn: Percent,
  product_management: Package,
  stock_management: Boxes,
  barcode_print: Barcode,
  purchases: Truck,
  reports: BarChart3,
  profit_loss: TrendingUp,
  promos: Percent,
  qris: QrCode,
  reconciliation: GitCompare,
  team_management: Users,
  audit_logs: History,
};

const valuePoints = [
  "Penjualan tunai dan QRIS dalam satu alur kasir",
  "Stok bergerak otomatis dari transaksi dan refund",
  "Low-stock alert, stock opname, dan histori pergerakan",
  "Paket fleksibel dengan kontrol outlet, pengguna, dan fitur",
];

const emptyContact = {
  name: "",
  business_name: "",
  email: "",
  phone: "",
  subject: "",
  message: "",
  website: "",
};

export default function Landing() {
  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const [plansError, setPlansError] = useState("");
  const [billingCycle, setBillingCycle] = useState("monthly");
  const [contact, setContact] = useState(emptyContact);
  const [contactLoading, setContactLoading] = useState(false);
  const [contactMessage, setContactMessage] = useState("");
  const [contactError, setContactError] = useState("");

  useEffect(() => {
    let mounted = true;
    api.get("/plans/public")
      .then((response) => {
        if (!mounted) return;
        setPlans((response.data || []).filter((plan) => plan.active !== false));
        setPlansError("");
      })
      .catch(() => {
        if (!mounted) return;
        setPlans([]);
        setPlansError("Daftar paket belum dapat dimuat. Silakan coba kembali beberapa saat lagi.");
      })
      .finally(() => {
        if (mounted) setPlansLoading(false);
      });
    return () => { mounted = false; };
  }, []);

  const publicFeatures = useMemo(() => {
    const featureMap = new Map();
    plans.forEach((plan) => {
      (plan.feature_details || []).forEach((feature) => {
        if (feature?.key && !featureMap.has(feature.key)) featureMap.set(feature.key, feature);
      });
    });
    return [...featureMap.values()];
  }, [plans]);

  const featureGroups = useMemo(() => {
    const groups = new Map();
    publicFeatures.forEach((feature) => {
      const category = feature.category || "Fitur Lainnya";
      if (!groups.has(category)) groups.set(category, []);
      groups.get(category).push(feature);
    });
    return [...groups.entries()].map(([name, features]) => ({ name, features }));
  }, [publicFeatures]);

  const maximumOutlets = useMemo(
    () => plans.reduce((highest, plan) => Math.max(highest, Number(plan.max_outlets || 0)), 0),
    [plans],
  );

  const submitContact = async (event) => {
    event.preventDefault();
    setContactLoading(true);
    setContactMessage("");
    setContactError("");
    try {
      const response = await api.post("/contact", {
        ...contact,
        name: contact.name.trim(),
        business_name: contact.business_name.trim() || null,
        email: contact.email.trim(),
        phone: contact.phone.trim() || null,
        subject: contact.subject.trim(),
        message: contact.message.trim(),
      });
      setContactMessage(response.data?.message || "Pesan berhasil dikirim.");
      setContact(emptyContact);
    } catch (error) {
      setContactError(error?.response?.data?.detail || "Pesan belum dapat dikirim. Silakan coba kembali.");
    } finally {
      setContactLoading(false);
    }
  };

  const updateContact = (field, value) => setContact((current) => ({ ...current, [field]: value }));

  return (
    <div className="min-h-screen overflow-x-hidden bg-slate-950 text-slate-100">
      <header className="sticky top-0 z-50 border-b border-white/10 bg-slate-950/80 backdrop-blur-xl">
        <div className="mx-auto flex h-18 max-w-7xl items-center justify-between px-5 py-4 lg:px-10">
          <Link to="/" aria-label="KRAYA home" data-testid="landing-brand">
            <BrandMark className="text-white" accentClassName="text-cyan-400" />
          </Link>
          <nav className="hidden items-center gap-8 text-sm text-slate-300 md:flex">
            <a href="#features" className="transition hover:text-white">Fitur</a>
            <a href="#pricing" className="transition hover:text-white">Paket</a>
            <a href="#comparison" className="transition hover:text-white">Perbandingan</a>
            <a href="#contact" className="transition hover:text-white">Hubungi Kami</a>
          </nav>
          <div className="flex items-center gap-2">
            <Link to="/login" data-testid="landing-login-link" className="rounded-xl px-4 py-2 text-sm font-semibold text-slate-200 transition hover:bg-white/10">
              Masuk
            </Link>
            <Link to="/register" data-testid="landing-register-cta" className="rounded-xl bg-cyan-400 px-4 py-2.5 text-sm font-bold text-slate-950 shadow-lg shadow-cyan-500/20 transition hover:-translate-y-0.5 hover:bg-cyan-300">
              Mulai Gratis
            </Link>
          </div>
        </div>
      </header>

      <main>
        <section className="relative isolate overflow-hidden border-b border-white/10">
          <div className="absolute inset-0 -z-20 bg-[radial-gradient(circle_at_15%_20%,rgba(34,211,238,0.20),transparent_32%),radial-gradient(circle_at_85%_25%,rgba(99,102,241,0.24),transparent_34%),linear-gradient(180deg,#020617_0%,#0f172a_100%)]" />
          <div className="absolute inset-0 -z-10 opacity-20 [background-image:linear-gradient(rgba(255,255,255,.08)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,.08)_1px,transparent_1px)] [background-size:48px_48px]" />
          <div className="mx-auto grid max-w-7xl gap-14 px-5 py-20 lg:grid-cols-[1.05fr_.95fr] lg:items-center lg:px-10 lg:py-28">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-cyan-300/20 bg-cyan-300/10 px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-cyan-200">
                <Sparkles className="h-3.5 w-3.5" /> POS modern untuk bisnis bertumbuh
              </div>
              <h1 className="mt-7 max-w-4xl font-display text-5xl font-black leading-[0.95] tracking-[-0.06em] text-white sm:text-6xl lg:text-7xl">
                Kendalikan penjualan, stok, dan outlet dari satu layar.
              </h1>
              <p className="mt-7 max-w-2xl text-lg leading-8 text-slate-300">
                KRAYA menyatukan kasir, QRIS, stok real-time, laporan, promo, pengguna, dan langganan dalam platform operasional multi-outlet yang lebih ringkas.
              </p>
              <div className="mt-9 flex flex-wrap gap-3">
                <Link to="/register" data-testid="hero-cta-trial" className="inline-flex items-center gap-2 rounded-2xl bg-white px-6 py-3.5 text-sm font-bold text-slate-950 shadow-2xl shadow-white/10 transition hover:-translate-y-0.5 hover:bg-cyan-50">
                  Mulai Trial 14 Hari <ArrowRight className="h-4 w-4" />
                </Link>
                <a href="#pricing" className="inline-flex items-center gap-2 rounded-2xl border border-white/20 bg-white/5 px-6 py-3.5 text-sm font-semibold text-white backdrop-blur transition hover:bg-white/10">
                  Lihat Paket
                </a>
              </div>
              <div className="mt-10 grid max-w-2xl grid-cols-3 gap-3">
                {[
                  { value: plansLoading ? "—" : plans.length, label: "Paket aktif" },
                  { value: plansLoading ? "—" : publicFeatures.length, label: "Fitur tersedia" },
                  { value: maximumOutlets || "—", label: "Outlet per paket" },
                ].map((item) => (
                  <div key={item.label} className="rounded-2xl border border-white/10 bg-white/[0.06] p-4 backdrop-blur">
                    <div className="font-display text-2xl font-black text-white sm:text-3xl">{item.value}</div>
                    <div className="mt-1 text-xs text-slate-400">{item.label}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative">
              <div className="absolute -inset-8 -z-10 rounded-full bg-indigo-500/20 blur-3xl" />
              <div className="rounded-[2rem] border border-white/15 bg-white/[0.08] p-3 shadow-2xl shadow-black/40 backdrop-blur-xl">
                <div className="rounded-[1.5rem] border border-white/10 bg-slate-950/90 p-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-xs uppercase tracking-[0.2em] text-slate-500">Ringkasan hari ini</div>
                      <div className="mt-1 font-display text-xl font-bold text-white">Outlet Overview</div>
                    </div>
                    <div className="flex items-center gap-2 rounded-full bg-emerald-400/10 px-3 py-1.5 text-xs font-semibold text-emerald-300">
                      <span className="h-2 w-2 rounded-full bg-emerald-400" /> Live
                    </div>
                  </div>
                  <div className="mt-5 grid grid-cols-2 gap-3">
                    <div className="rounded-2xl bg-gradient-to-br from-cyan-400 to-blue-500 p-5 text-slate-950">
                      <div className="text-xs font-semibold uppercase tracking-wider opacity-70">Penjualan</div>
                      <div className="mt-2 font-display text-3xl font-black">Rp 8,4 jt</div>
                      <div className="mt-4 text-xs font-semibold">+18,2% dari kemarin</div>
                    </div>
                    <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
                      <div className="text-xs uppercase tracking-wider text-slate-500">Transaksi</div>
                      <div className="mt-2 font-display text-3xl font-black text-white">187</div>
                      <div className="mt-4 flex items-center gap-2 text-xs text-slate-400"><QrCode className="h-4 w-4 text-indigo-300" /> 62% QRIS</div>
                    </div>
                  </div>
                  <div className="mt-3 grid gap-3 sm:grid-cols-3">
                    {[
                      { icon: Boxes, title: "Stok real-time", value: "1.284 item", tone: "text-cyan-300" },
                      { icon: Zap, title: "Stok rendah", value: "7 produk", tone: "text-amber-300" },
                      { icon: Store, title: "Outlet aktif", value: "5 lokasi", tone: "text-indigo-300" },
                    ].map((item) => (
                      <div key={item.title} className="rounded-2xl border border-white/10 bg-white/[0.04] p-4">
                        <item.icon className={`h-5 w-5 ${item.tone}`} />
                        <div className="mt-3 text-xs text-slate-500">{item.title}</div>
                        <div className="mt-1 text-sm font-bold text-white">{item.value}</div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-3 rounded-2xl border border-white/10 bg-white/[0.04] p-4">
                    <div className="flex items-center justify-between text-xs text-slate-400">
                      <span>Performa penjualan 7 hari</span><span>Realtime</span>
                    </div>
                    <div className="mt-5 flex h-24 items-end gap-2">
                      {[38, 58, 45, 72, 64, 88, 76, 96, 82, 100, 92, 110].map((height, index) => (
                        <div key={index} className="flex-1 rounded-t bg-gradient-to-t from-indigo-600 to-cyan-300" style={{ height: `${height * 0.72}px`, opacity: 0.55 + index * 0.035 }} />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="border-b border-slate-200 bg-white py-20 text-slate-950 lg:py-28">
          <div className="mx-auto grid max-w-7xl gap-12 px-5 lg:grid-cols-[.8fr_1.2fr] lg:px-10">
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.22em] text-blue-600">Operasional lebih terkendali</div>
              <h2 className="mt-4 font-display text-4xl font-black tracking-[-0.05em] sm:text-5xl">Bukan sekadar kasir. Ini pusat kendali bisnis.</h2>
              <p className="mt-5 text-lg leading-8 text-slate-600">Setiap transaksi langsung terhubung ke stok, laporan, audit, dan rekonsiliasi sehingga tim dapat bekerja dengan data yang sama.</p>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              {valuePoints.map((point, index) => (
                <div key={point} className="rounded-3xl border border-slate-200 bg-slate-50 p-6 transition hover:-translate-y-1 hover:border-blue-200 hover:bg-blue-50/50">
                  <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-slate-950 font-display text-sm font-black text-white">0{index + 1}</div>
                  <p className="mt-5 font-semibold leading-7 text-slate-800">{point}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="features" className="border-b border-white/10 bg-slate-950 py-20 lg:py-28">
          <div className="mx-auto max-w-7xl px-5 lg:px-10">
            <div className="flex flex-col gap-5 md:flex-row md:items-end md:justify-between">
              <div>
                <div className="text-xs font-bold uppercase tracking-[0.22em] text-cyan-300">Seluruh fitur aktif</div>
                <h2 className="mt-4 max-w-3xl font-display text-4xl font-black tracking-[-0.05em] text-white sm:text-5xl">Fitur mengikuti konfigurasi paket di database.</h2>
              </div>
              <p className="max-w-xl text-sm leading-7 text-slate-400">Daftar berikut dibentuk otomatis dari seluruh fitur yang digunakan oleh paket aktif. Perubahan plan oleh Platform Owner akan langsung tercermin di halaman ini.</p>
            </div>

            {plansLoading && <div className="mt-10 grid gap-4 md:grid-cols-2 lg:grid-cols-3">{[0,1,2,3,4,5].map((item) => <div key={item} className="h-48 animate-pulse rounded-3xl bg-white/5" />)}</div>}
            {!plansLoading && plansError && <div className="mt-10 rounded-3xl border border-rose-400/20 bg-rose-400/10 p-6 text-sm text-rose-200">{plansError}</div>}
            {!plansLoading && !plansError && publicFeatures.length === 0 && <div className="mt-10 rounded-3xl border border-dashed border-white/15 p-8 text-center text-sm text-slate-400">Belum ada fitur pada paket aktif.</div>}

            {!plansLoading && !plansError && featureGroups.length > 0 && (
              <div className="mt-12 space-y-10">
                {featureGroups.map((group) => (
                  <div key={group.name}>
                    <div className="mb-4 flex items-center gap-3">
                      <span className="h-px w-8 bg-cyan-400" />
                      <h3 className="text-sm font-bold uppercase tracking-[0.2em] text-slate-300">{group.name}</h3>
                    </div>
                    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                      {group.features.map((feature) => {
                        const Icon = featureIcons[feature.key] || Sparkles;
                        return (
                          <article key={feature.key} className="group rounded-3xl border border-white/10 bg-white/[0.045] p-6 transition hover:-translate-y-1 hover:border-cyan-300/30 hover:bg-white/[0.075]">
                            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-cyan-300/20 to-indigo-400/20 text-cyan-200">
                              <Icon className="h-6 w-6" />
                            </div>
                            <h4 className="mt-5 font-display text-xl font-bold text-white">{feature.label}</h4>
                            <p className="mt-2 text-sm leading-6 text-slate-400">{feature.description || "Tersedia sesuai paket berlangganan yang dipilih."}</p>
                          </article>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        <section id="pricing" className="border-b border-slate-200 bg-slate-50 py-20 text-slate-950 lg:py-28">
          <div className="mx-auto max-w-7xl px-5 lg:px-10">
            <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <div className="text-xs font-bold uppercase tracking-[0.22em] text-blue-600">Paket berlangganan</div>
                <h2 className="mt-4 max-w-3xl font-display text-4xl font-black tracking-[-0.05em] sm:text-5xl">Harga jujur. Tanpa biaya tersembunyi.</h2>
                <p className="mt-4 max-w-2xl leading-7 text-slate-600">Seluruh paket aktif, harga, batas outlet, batas pengguna, dan fitur ditampilkan langsung dari database KRAYA.</p>
              </div>
              <div className="inline-flex w-fit rounded-2xl border border-slate-200 bg-white p-1 shadow-sm">
                <button type="button" onClick={() => setBillingCycle("monthly")} className={`rounded-xl px-4 py-2 text-sm font-semibold transition ${billingCycle === "monthly" ? "bg-slate-950 text-white" : "text-slate-500 hover:text-slate-950"}`}>Bulanan</button>
                <button type="button" onClick={() => setBillingCycle("yearly")} className={`rounded-xl px-4 py-2 text-sm font-semibold transition ${billingCycle === "yearly" ? "bg-slate-950 text-white" : "text-slate-500 hover:text-slate-950"}`}>Tahunan</button>
              </div>
            </div>

            {plansLoading && <div className="mt-10 grid gap-5 md:grid-cols-2 xl:grid-cols-3" data-testid="public-plans-loading">{[0,1,2].map((item) => <div key={item} className="h-[520px] animate-pulse rounded-[2rem] bg-slate-200/70" />)}</div>}
            {!plansLoading && plansError && <div className="mt-10 rounded-3xl border border-rose-200 bg-rose-50 p-6 text-sm text-rose-700" data-testid="public-plans-error">{plansError}</div>}
            {!plansLoading && !plansError && plans.length === 0 && <div className="mt-10 rounded-3xl border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-500">Belum ada paket aktif yang tersedia.</div>}

            {!plansLoading && !plansError && plans.length > 0 && (
              <div className="mt-10 grid items-stretch gap-5 md:grid-cols-2 xl:grid-cols-3">
                {plans.map((plan) => {
                  const price = billingCycle === "yearly" ? plan.price_yearly : plan.price_monthly;
                  return (
                    <article key={plan.id} className="flex flex-col rounded-[2rem] border border-slate-200 bg-white p-7 shadow-sm transition hover:-translate-y-1 hover:border-blue-300 hover:shadow-xl hover:shadow-blue-900/5" data-testid={`public-plan-${plan.id}`}>
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600">Paket</div>
                          <h3 className="mt-2 font-display text-3xl font-black tracking-[-0.04em]">{plan.name}</h3>
                        </div>
                        <div className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-bold text-emerald-700">Aktif</div>
                      </div>
                      <div className="mt-7">
                        <span className="font-display text-4xl font-black tracking-[-0.05em] tabular-nums">{formatIDR(price)}</span>
                        <span className="ml-1 text-sm text-slate-500">/{billingCycle === "yearly" ? "tahun" : "bulan"}</span>
                      </div>
                      <div className="mt-6 grid grid-cols-2 gap-3">
                        <div className="rounded-2xl bg-slate-100 p-4"><div className="text-xs text-slate-500">Outlet aktif</div><div className="mt-1 font-display text-2xl font-black">{plan.max_outlets}</div></div>
                        <div className="rounded-2xl bg-slate-100 p-4"><div className="text-xs text-slate-500">Pengguna aktif</div><div className="mt-1 font-display text-2xl font-black">{plan.max_users}</div></div>
                      </div>
                      <div className="my-6 h-px bg-slate-200" />
                      <div className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500">Semua fitur paket</div>
                      <ul className="mt-4 flex-1 space-y-3 text-sm">
                        {(plan.feature_details || []).map((feature) => (
                          <li key={feature.key} className="flex items-start gap-3 text-slate-700"><span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-blue-50 text-blue-600"><Check className="h-3.5 w-3.5" /></span><span>{feature.label}</span></li>
                        ))}
                      </ul>
                      <Link to={`/register?plan=${encodeURIComponent(plan.id)}`} data-testid={`pricing-cta-${String(plan.name || plan.id).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="mt-7 inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-slate-950 px-5 py-3.5 text-sm font-bold text-white transition hover:bg-blue-600">
                        Pilih {plan.name} <ArrowRight className="h-4 w-4" />
                      </Link>
                    </article>
                  );
                })}
              </div>
            )}
          </div>
        </section>

        {!plansLoading && !plansError && plans.length > 0 && (
          <section id="comparison" className="border-b border-slate-200 bg-white py-20 text-slate-950 lg:py-28">
            <div className="mx-auto max-w-7xl px-5 lg:px-10">
              <div className="mb-8">
                <div className="text-xs font-bold uppercase tracking-[0.22em] text-blue-600">Matriks lengkap</div>
                <h2 className="mt-4 font-display text-4xl font-black tracking-[-0.05em] sm:text-5xl">Bandingkan plan dan fitur.</h2>
                <p className="mt-4 text-slate-600">Geser tabel pada layar kecil untuk melihat seluruh paket aktif.</p>
              </div>
              <div className="overflow-hidden rounded-[2rem] border border-slate-200 shadow-sm">
                <PlanFeatureMatrix plans={plans} features={publicFeatures} />
              </div>
            </div>
          </section>
        )}

        <section id="contact" className="border-b border-slate-200 bg-slate-100 py-20 text-slate-950 lg:py-28">
          <div className="mx-auto grid max-w-7xl gap-10 px-5 lg:grid-cols-[0.85fr_1.15fr] lg:px-10">
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.22em] text-blue-600">Contact Us</div>
              <h2 className="mt-4 font-display text-4xl font-black tracking-[-0.05em] sm:text-5xl">Diskusikan kebutuhan bisnis Anda.</h2>
              <p className="mt-5 max-w-xl leading-7 text-slate-600">Tim bisnis KRAYA siap membantu pemilihan paket, kebutuhan multi-outlet, QRIS, migrasi data, dan penggunaan aplikasi.</p>
              <div className="mt-8 space-y-4">
                <div className="flex items-start gap-4 rounded-2xl border border-slate-200 bg-white p-5">
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-600"><Mail className="h-5 w-5" /></div>
                  <div><div className="text-sm font-bold">Email bisnis</div><a href="mailto:bisnis@kraya.co.id" className="mt-1 block text-sm text-blue-600 hover:underline">bisnis@kraya.co.id</a></div>
                </div>
                <div className="flex items-start gap-4 rounded-2xl border border-slate-200 bg-white p-5">
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-cyan-50 text-cyan-700"><MessageSquare className="h-5 w-5" /></div>
                  <div><div className="text-sm font-bold">Respons tim KRAYA</div><div className="mt-1 text-sm leading-6 text-slate-500">Pesan dari formulir ini langsung diteruskan ke tim bisnis KRAYA.</div></div>
                </div>
              </div>
            </div>

            <form onSubmit={submitContact} className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-xl shadow-slate-900/5 sm:p-8" data-testid="contact-form">
              <div className="grid gap-5 sm:grid-cols-2">
                <label className="block">
                  <span className="text-sm font-bold text-slate-700">Nama lengkap</span>
                  <div className="relative mt-2"><input required minLength={2} maxLength={120} value={contact.name} onChange={(event) => updateContact("name", event.target.value)} className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3.5 pl-11 text-sm outline-none focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-100" placeholder="Nama Anda" /><Users className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" /></div>
                </label>
                <label className="block">
                  <span className="text-sm font-bold text-slate-700">Nama usaha</span>
                  <div className="relative mt-2"><input maxLength={160} value={contact.business_name} onChange={(event) => updateContact("business_name", event.target.value)} className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3.5 pl-11 text-sm outline-none focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-100" placeholder="Nama bisnis" /><Building2 className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" /></div>
                </label>
                <label className="block">
                  <span className="text-sm font-bold text-slate-700">Email</span>
                  <div className="relative mt-2"><input required type="email" maxLength={254} value={contact.email} onChange={(event) => updateContact("email", event.target.value)} className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3.5 pl-11 text-sm outline-none focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-100" placeholder="nama@bisnis.com" /><Mail className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" /></div>
                </label>
                <label className="block">
                  <span className="text-sm font-bold text-slate-700">Nomor HP</span>
                  <div className="relative mt-2"><input maxLength={30} value={contact.phone} onChange={(event) => updateContact("phone", event.target.value)} className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3.5 pl-11 text-sm outline-none focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-100" placeholder="08xxxxxxxxxx" /><Phone className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" /></div>
                </label>
                <label className="block sm:col-span-2">
                  <span className="text-sm font-bold text-slate-700">Topik</span>
                  <input required minLength={3} maxLength={160} value={contact.subject} onChange={(event) => updateContact("subject", event.target.value)} className="mt-2 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3.5 text-sm outline-none focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-100" placeholder="Contoh: Kebutuhan POS untuk 5 outlet" />
                </label>
                <label className="block sm:col-span-2">
                  <span className="text-sm font-bold text-slate-700">Pesan</span>
                  <textarea required minLength={10} maxLength={3000} rows={6} value={contact.message} onChange={(event) => updateContact("message", event.target.value)} className="mt-2 w-full resize-y rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3.5 text-sm leading-6 outline-none focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-100" placeholder="Ceritakan kebutuhan usaha Anda..." />
                </label>
                <div className="hidden" aria-hidden="true"><label>Website<input tabIndex={-1} autoComplete="off" value={contact.website} onChange={(event) => updateContact("website", event.target.value)} /></label></div>
              </div>
              {contactMessage && <div className="mt-5 rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-sm font-semibold text-emerald-700" data-testid="contact-success">{contactMessage}</div>}
              {contactError && <div className="mt-5 rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm font-semibold text-rose-700" data-testid="contact-error">{contactError}</div>}
              <button type="submit" disabled={contactLoading} className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-slate-950 px-6 py-4 text-sm font-bold text-white transition hover:bg-blue-600 disabled:cursor-not-allowed disabled:opacity-50" data-testid="contact-submit">
                <Send className="h-4 w-4" /> {contactLoading ? "Mengirim…" : "Kirim Pesan"}
              </button>
              <p className="mt-4 text-center text-xs leading-5 text-slate-400">Dengan mengirim pesan, Anda menyetujui tim KRAYA menghubungi Anda melalui email atau nomor HP yang diberikan.</p>
            </form>
          </div>
        </section>

        <section className="bg-gradient-to-br from-blue-600 via-indigo-600 to-violet-700 py-20 lg:py-24">
          <div className="mx-auto flex max-w-5xl flex-col items-center px-5 text-center">
            <div className="rounded-full bg-white/10 px-4 py-2 text-xs font-bold uppercase tracking-[0.2em] text-blue-100">Mulai tanpa kartu kredit</div>
            <h2 className="mt-6 font-display text-4xl font-black tracking-[-0.05em] text-white sm:text-5xl">Buat operasional outlet lebih sederhana bersama KRAYA.</h2>
            <p className="mt-5 max-w-2xl text-lg leading-8 text-blue-100">Aktifkan trial 14 hari, konfigurasi outlet, dan mulai transaksi pertama Anda.</p>
            <Link to="/register" className="mt-8 inline-flex items-center gap-2 rounded-2xl bg-white px-7 py-4 text-sm font-bold text-slate-950 shadow-xl transition hover:-translate-y-0.5 hover:bg-cyan-50">Daftar Sekarang <ArrowRight className="h-4 w-4" /></Link>
          </div>
        </section>
      </main>

      <footer className="border-t border-white/10 bg-slate-950">
        <div className="mx-auto grid max-w-7xl gap-10 px-5 py-14 md:grid-cols-4 lg:px-10">
          <div className="md:col-span-2">
            <BrandMark size="lg" className="text-white" accentClassName="text-cyan-400" />
            <p className="mt-4 max-w-md text-sm leading-7 text-slate-400">Platform POS multi-outlet untuk penjualan, stok, QRIS, laporan, dan pengelolaan operasional bisnis.</p>
          </div>
          <div><div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Produk</div><div className="mt-4 space-y-3 text-sm text-slate-300"><a className="block hover:text-white" href="#features">Fitur</a><a className="block hover:text-white" href="#pricing">Paket</a><a className="block hover:text-white" href="#comparison">Perbandingan</a><a className="block hover:text-white" href="#contact">Hubungi kami</a></div></div>
          <div><div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Akun</div><div className="mt-4 space-y-3 text-sm text-slate-300"><Link className="block hover:text-white" to="/login">Masuk</Link><Link className="block hover:text-white" to="/register">Daftar mitra</Link></div></div>
        </div>
        <div className="border-t border-white/10 px-5 py-5 text-center text-xs text-slate-500">© {new Date().getFullYear()} KRAYA. <span className="text-slate-300">Powered By AntSoft ID</span></div>
      </footer>
    </div>
  );
}
